import * as React from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import { CalendarGrid } from "./CalendarGrid";
import { TimeInput } from "./TimeInput";
import type { CalendarBoxProps, YearMonth } from "../types";
import "./CalendarBox.css";

// =============================================================================
// CalendarBox Component
// Calendar with navigation and optional time input
// =============================================================================

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const MONTH_SHORT_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const CalendarBox = React.memo(function CalendarBox({
  year,
  month,
  onMonthChange,
  selectedDate,
  rangeStart,
  rangeEnd,
  hoveredDate,
  editingField,
  minDate,
  maxDate,
  minMonth,
  maxMonth,
  onDateClick,
  onDateHover,
  showTime = false,
  timeValue = "",
  onTimeChange,
  onTimeBlur,
  isTimeError = false,
  previousTimeValue,
  onTimeUndo,
  timeGranularity = "minute",
  timeLabel,
  timezone = "UTC",
  actionTooltip,
  showOutsideDays = true,
  className,
}: CalendarBoxProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [dropdownYear, setDropdownYear] = React.useState(year);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Navigation Helpers
  // ---------------------------------------------------------------------------

  const canGoPrevious = React.useMemo(() => {
    if (!minMonth) return true;
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonth = month === 1 ? 12 : month - 1;
    return isMonthAfterOrEqual({ year: prevYear, month: prevMonth }, minMonth);
  }, [year, month, minMonth]);

  const canGoNext = React.useMemo(() => {
    if (!maxMonth) return true;
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonth = month === 12 ? 1 : month + 1;
    return isMonthBeforeOrEqual({ year: nextYear, month: nextMonth }, maxMonth);
  }, [year, month, maxMonth]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handlePrevious = React.useCallback(() => {
    if (!canGoPrevious) return;
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonth = month === 1 ? 12 : month - 1;
    onMonthChange(prevYear, prevMonth);
  }, [year, month, canGoPrevious, onMonthChange]);

  const handleNext = React.useCallback(() => {
    if (!canGoNext) return;
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonth = month === 12 ? 1 : month + 1;
    onMonthChange(nextYear, nextMonth);
  }, [year, month, canGoNext, onMonthChange]);

  const handleHeaderClick = React.useCallback(() => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    } else {
      setDropdownYear(year);
      setDropdownOpen(true);
    }
  }, [year, dropdownOpen]);

  const handleMonthSelect = React.useCallback((selectedMonth: number) => {
    onMonthChange(dropdownYear, selectedMonth);
    setDropdownOpen(false);
  }, [dropdownYear, onMonthChange]);

  const handleYearSelect = React.useCallback((selectedYear: number) => {
    onMonthChange(selectedYear, month);
    setDropdownOpen(false);
  }, [month, onMonthChange]);

  const handleDropdownKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // ---------------------------------------------------------------------------
  // Generate Years for Dropdown
  // ---------------------------------------------------------------------------

  const dropdownYears = React.useMemo(() => {
    const years: number[] = [];
    for (let y = 2020; y <= 2027; y++) {
      years.push(y);
    }
    return years;
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`calendar-box ${className || ""}`}>
      {/* Header with Navigation */}
      <div className="calendar-box__header">
        <button
          type="button"
          className="calendar-box__nav-button"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>

        <div className="calendar-box__header-center">
          <button
            type="button"
            className="calendar-box__header-button"
            onClick={handleHeaderClick}
            aria-label={`${MONTH_NAMES[month - 1]} ${year}`}
          >
            <span className="calendar-box__month-name">{MONTH_NAMES[month - 1]}</span>
            <span className="calendar-box__year">{year}</span>
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div
              ref={dropdownRef}
              className="calendar-box__dropdown"
              role="listbox"
              onKeyDown={handleDropdownKeyDown}
            >
              {/* Year Selector */}
              <div className="calendar-box__dropdown-years">
                {dropdownYears.map((y) => (
                  <button
                    key={y}
                    type="button"
                    className={`calendar-box__dropdown-year ${y === dropdownYear ? "calendar-box__dropdown-year--active" : ""}`}
                    onClick={() => handleYearSelect(y)}
                  >
                    {y}
                  </button>
                ))}
              </div>

              {/* Month Grid */}
              <div className="calendar-box__dropdown-months">
                {MONTH_SHORT_NAMES.map((m, index) => (
                  <button
                    key={m}
                    type="button"
                    className={`calendar-box__dropdown-month ${index + 1 === month && dropdownYear === year ? "calendar-box__dropdown-month--active" : ""}`}
                    onClick={() => handleMonthSelect(index + 1)}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="calendar-box__nav-button"
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Calendar Grid */}
      <CalendarGrid
        year={year}
        month={month}
        selectedDate={selectedDate}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        hoveredDate={hoveredDate}
        editingField={editingField}
        minDate={minDate}
        maxDate={maxDate}
        onDateClick={onDateClick}
        onDateHover={onDateHover}
        timezone={timezone}
        showOutsideDays={showOutsideDays}
        actionTooltip={actionTooltip}
      />

      {/* Time Input */}
      {showTime && (
        <div className="calendar-box__time">
          <TimeInput
            value={timeValue}
            onChange={onTimeChange || (() => {})}
            onBlur={onTimeBlur}
            granularity={timeGranularity === "date" ? "minute" : timeGranularity}
            isError={isTimeError}
            label={timeLabel}
            showScrollOverlay
            previousValue={previousTimeValue}
            onUndo={onTimeUndo}
          />
        </div>
      )}

      {/* Selection hint for range picker */}
      {editingField && (
        <div className="calendar-box__selection-hint">
          Selecting {editingField === "from" ? "From" : "To"} date
        </div>
      )}
    </div>
  );
});

// =============================================================================
// Helper Functions
// =============================================================================

function isMonthAfterOrEqual(a: YearMonth, b: YearMonth): boolean {
  if (a.year > b.year) return true;
  if (a.year < b.year) return false;
  return a.month >= b.month;
}

function isMonthBeforeOrEqual(a: YearMonth, b: YearMonth): boolean {
  if (a.year < b.year) return true;
  if (a.year > b.year) return false;
  return a.month <= b.month;
}
